import { useMemo, useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import CloseIcon from "../../../../../assets/svg/close-icon";
import CircularArrowBackIcon from "../../../../../assets/svg/circular-arrow-back-icon";
import PlusIcon from "../../../../../assets/svg/plus-icon";
import SearchIcon from "../../../../../assets/svg/search-icon";
import { TruncatedTextTooltip } from "../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { Tooltip } from "../../../../../components/Tooltip/Tooltip";
import Button from "../../../../../components/Button/Button";
import ChevronIcon from "../../../../../assets/svg/chevron-icon";
import { CrmFilterGroupBlock } from "./CrmFilterGroupBlock";
import {
  type FilterGroupUI,
  createEmptyGroup,
  getCrmViewFilterFieldOptions,
  CRM_FIELD_DEPENDENCIES,
  getCrmOperatorsForField,
  getSelectedValuesForField,
} from "../utlis/crmViewConstants";
import type {
  FilterFieldOption,
  FilterObjectType,
} from "../../HelpDesk/components/CreateView/utils/constants";
import { RootState } from "../../../../../redux/store";
import { getClientCRMFlag } from "../../../../../utils/constants/clientCustomCRMFlags";

interface CrmCreateViewFiltersProps {
  filterGroups: FilterGroupUI[];
  logicalOperator: "and" | "or";
  onFilterGroupsChange: (groups: FilterGroupUI[]) => void;
  onLogicalOperatorChange: (op: "and" | "or") => void;
  onBack: () => void;
  onClose: () => void;
  onSave: () => void;
  isEditMode?: boolean;
  isSaveViewLoading?: boolean;
  hasUnsavedChanges?: boolean;
  saveButtonText?: string;
  extraFieldOptions?: FilterFieldOption[];
  extraOperators?: Record<string, { key: string; label: string }[]>;
  extraValueOptions?: Record<string, { key: string; label: string }[]>;
  extraSingleValueFields?: string[];
  extraFreeTextFields?: string[];
  showSectionedPicker?: boolean;
}

const getSectionLabel = (section: FilterObjectType): string => {
  if (section === "lead") {
    return getClientCRMFlag("useContactLabel") ? "Contacts" : "Leads";
  }
  return "Bookings";
};

const SECTION_ORDER: FilterObjectType[] = ["lead", "deal"];

const AddFilterDropdown = ({
  onSelect,
  filterGroups = [],
  fieldOptions,
  showSectioned = false,
}: {
  onSelect: (fieldKey: string, section: FilterObjectType) => void;
  filterGroups?: FilterGroupUI[];
  fieldOptions: FilterFieldOption[];
  showSectioned?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const disabledFields = useMemo(() => {
    const map: Record<string, string> = {};
    for (const [fieldKey, dep] of Object.entries(CRM_FIELD_DEPENDENCIES)) {
      const parentValues = getSelectedValuesForField(
        filterGroups,
        dep.parentField
      );
      if (parentValues.length === 0) {
        map[fieldKey] = dep.tooltip;
      }
    }
    return map;
  }, [filterGroups]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [open]);

  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return fieldOptions;
    return fieldOptions.filter((f) => f.label.toLowerCase().includes(q));
  }, [fieldOptions, searchQuery]);

  const sectionedOptions = useMemo(() => {
    if (!showSectioned) {
      return [{ section: "lead" as FilterObjectType, items: filteredOptions }];
    }
    const groups: Record<FilterObjectType, FilterFieldOption[]> = {
      lead: [],
      deal: [],
    };
    filteredOptions.forEach((f) => {
      const section: FilterObjectType = f.section ?? "lead";
      groups[section].push(f);
    });
    return SECTION_ORDER.filter((s) => groups[s].length > 0).map((section) => ({
      section,
      items: groups[section],
    }));
  }, [filteredOptions, showSectioned]);

  const handleToggle = () => {
    setOpen((p) => {
      if (p) setSearchQuery("");
      return !p;
    });
  };

  const handlePick = (field: FilterFieldOption) => {
    if (disabledFields[field.key]) return;
    const section: FilterObjectType = field.section ?? "lead";
    onSelect(field.key, section);
    setOpen(false);
    setSearchQuery("");
  };

  return (
    <div ref={wrapperRef} className="relative w-auto">
      <div onClick={handleToggle} className="cursor-pointer">
        <div className="bg-verba-indigo dark:bg-verba-indigo-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk rounded-full w-[60px] h-[48px] flex items-center justify-center shadow-card border-[2.5px] border-border-gray dark:border-border-gray-dk hover:scale-105 transition-transform">
          <PlusIcon
            className="w-5 h-5 text-pure-white dark:text-pure-white-dk"
            strokeWidth={1.1}
            stroke="currentColor"
          />
        </div>
      </div>
      {open && (
        <div className="absolute left-0 top-[55px] z-50 w-[220px] max-h-[400px] bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg shadow-popper-blur-effect flex flex-col overflow-hidden">
          <div className="px-3 py-2 flex items-center gap-1.5 border-b border-border-gray dark:border-border-gray-dk">
            <SearchIcon className="w-[14px] h-[14px] text-heading-text dark:text-heading-text-dk shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="w-full text-md bg-transparent border-none outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {sectionedOptions.length === 0 ? (
              <div className="p-3 text-md text-gray-placeholder dark:text-gray-placeholder-dk text-center">
                No results found
              </div>
            ) : (
              sectionedOptions.map(({ section, items }) => (
                <div key={section}>
                  {showSectioned && (
                    <div className="px-3 pt-1.5 pb-0.5 flex flex-col items-start">
                      <span className="text-xxs font-semibold uppercase text-dark-gray dark:text-dark-gray-dk">
                        {getSectionLabel(section)}
                      </span>
                    </div>
                  )}
                  {items.map((field) => {
                    const Icon = field.icon;
                    const tooltipText = disabledFields[field.key];
                    const row = (
                      <button
                        key={field.key}
                        type="button"
                        onClick={() => handlePick(field)}
                        disabled={Boolean(tooltipText)}
                        className={`w-full flex items-center gap-1.5 px-3 py-2.5 text-md hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors cursor-pointer text-verba-indigo dark:text-verba-indigo-dk ${tooltipText ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <Icon
                          className={`shrink-0 text-verba-indigo dark:text-verba-indigo-dk ${
                            field.iconClassName ??
                            (showSectioned
                              ? "w-[16px] h-[16px]"
                              : "w-[18px] h-[18px]")
                          }`}
                        />
                        <TruncatedTextTooltip
                          text={field.label}
                          maxWidth="max-w-[150px]"
                        />
                      </button>
                    );
                    if (tooltipText) {
                      return (
                        <Tooltip
                          key={field.key}
                          content={tooltipText}
                          side="right"
                        >
                          {row}
                        </Tooltip>
                      );
                    }
                    return row;
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TopLevelLogicalToggle = ({
  value,
  onChange,
  disabled = false,
}: {
  value: "and" | "or";
  onChange: (val: "and" | "or") => void;
  disabled?: boolean;
}) => {
  if (disabled) {
    return (
      <span className="px-2 py-1 text-sm font-medium text-gray-placeholder dark:text-gray-placeholder-dk uppercase select-none">
        {value?.toUpperCase()}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onChange(value === "and" ? "or" : "and")}
      className="flex items-center gap-0.5 px-2 py-1 border border-border-gray dark:border-border-gray-dk rounded-md text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk bg-pure-white dark:bg-pure-white-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk cursor-pointer transition-colors uppercase"
    >
      {value?.toUpperCase()}
      <ChevronIcon className="w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk" />
    </button>
  );
};

export const CrmCreateViewFilters = ({
  filterGroups,
  logicalOperator,
  onFilterGroupsChange,
  onLogicalOperatorChange,
  onBack,
  onClose,
  onSave,
  isEditMode = false,
  isSaveViewLoading = false,
  hasUnsavedChanges = true,
  saveButtonText = "Save View",
  extraFieldOptions,
  extraOperators,
  extraValueOptions,
  extraSingleValueFields,
  extraFreeTextFields,
  showSectionedPicker = false,
}: CrmCreateViewFiltersProps) => {
  const clientCode = useSelector(
    (state: RootState) => state?.auth?.client?.client_code
  );
  const fieldOptions = useMemo<FilterFieldOption[]>(() => {
    const base: FilterFieldOption[] = [
      ...getCrmViewFilterFieldOptions(clientCode),
    ];
    const extras = extraFieldOptions ?? [];
    for (const extra of extras) {
      const existingIdx = base.findIndex((f) => f.key === extra.key);
      if (existingIdx !== -1) {
        // Extra with the same key overrides the base entry (label, icon, ...).
        base.splice(existingIdx, 1, extra);
        continue;
      }
      const insertAfter = extra.insertAfterKey;
      if (insertAfter) {
        const idx = base.findIndex((f) => f.key === insertAfter);
        if (idx !== -1) {
          base.splice(idx + 1, 0, extra);
          continue;
        }
      }
      base.push(extra);
    }
    return base;
  }, [clientCode, extraFieldOptions]);
  const hasFilters = filterGroups?.length > 0;

  const hasValidFilters = useMemo(
    () =>
      filterGroups?.some((g) =>
        g?.conditions?.some((c) => c?.key && c?.value?.length > 0)
      ) ?? false,
    [filterGroups]
  );

  const handleFooterCancel = () => {
    if (isEditMode) {
      onClose();
    } else {
      onBack();
    }
  };

  const resolveOperatorForField = (fieldKey: string): string => {
    const extra = extraOperators?.[fieldKey];
    if (extra && extra.length > 0) return extra[0].key;
    return getCrmOperatorsForField(fieldKey)[0]?.key ?? "contains";
  };

  const handleAddFilterFromEmpty = (
    fieldKey: string,
    section: FilterObjectType
  ) => {
    const newGroup = createEmptyGroup(section);
    newGroup.conditions[0].key = fieldKey;
    newGroup.conditions[0].comparison_operator =
      resolveOperatorForField(fieldKey);
    newGroup.conditions[0].object_type = section;
    onFilterGroupsChange([...filterGroups, newGroup]);
  };

  const handleAddFilter = () => {
    onFilterGroupsChange([...filterGroups, createEmptyGroup("lead")]);
  };

  const handleGroupChange = (idx: number, updated: FilterGroupUI) => {
    const newGroups = [...filterGroups];
    newGroups[idx] = updated;
    onFilterGroupsChange(newGroups);
  };

  const handleDeleteGroup = (idx: number) => {
    onFilterGroupsChange(filterGroups?.filter((_, i) => i !== idx));
  };

  const handleClearAll = () => {
    onFilterGroupsChange([]);
  };

  return (
    <div className="flex flex-col h-full bg-pure-white dark:bg-pure-white-dk overflow-hidden rounded-l-[8px]">
      <div className="border-b border-border-gray dark:border-border-gray-dk flex items-center justify-between px-3 h-[50px] shrink-0 rounded-tl-[8px]">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center justify-center w-6 h-6 rounded-full hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors cursor-pointer"
            aria-label="Back to view details"
          >
            <CircularArrowBackIcon className="w-5 h-5 text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
          <span className="text-base font-semibold text-verba-indigo dark:text-verba-indigo-dk">
            Filters
          </span>
        </div>
        <div className="flex items-center gap-4">
          {hasFilters && (
            <button
              onClick={handleClearAll}
              className="h-[26px] px-3 flex items-center justify-center border border-verba-red-alert dark:border-verba-red-alert-dk rounded-md text-xs font-medium text-verba-red-alert dark:text-verba-red-alert-dk hover:bg-verba-red-alert dark:hover:bg-verba-red-alert-dk hover:text-fixed-white dark:hover:text-fixed-white-dk transition-colors cursor-pointer"
            >
              Clear All
            </button>
          )}
          <button
            onClick={onClose}
            className="w-[18px] h-[18px] bg-tag-gray dark:bg-tag-gray-dk rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer"
            aria-label="Close"
          >
            <CloseIcon className="w-[6px] h-[6px] text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
        </div>
      </div>

      <div className="relative flex-1 overflow-y-auto">
        {!hasFilters ? (
          <div className="absolute top-[15%] left-1/2 -translate-x-1/2 flex flex-col items-center gap-6 px-4">
            <AddFilterDropdown
              onSelect={handleAddFilterFromEmpty}
              filterGroups={filterGroups}
              fieldOptions={fieldOptions}
              showSectioned={showSectionedPicker}
            />
            <span className="text-lg text-verba-indigo dark:text-verba-indigo-dk">
              Click to add a &quot;Filter&quot;
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-3 px-4 py-6">
            {filterGroups?.map((group, gIdx) => (
              <div
                key={gIdx}
                className={`flex ${group?.conditions?.length > 1 ? "items-center" : "items-start"}`}
              >
                {gIdx > 0 && (
                  <div
                    className={`${group?.conditions?.length > 1 ? "" : "pt-3"} w-[70px] shrink-0`}
                  >
                    <TopLevelLogicalToggle
                      value={logicalOperator}
                      onChange={onLogicalOperatorChange}
                      disabled={gIdx > 1}
                    />
                  </div>
                )}
                <CrmFilterGroupBlock
                  group={group}
                  onGroupChange={(updated) => handleGroupChange(gIdx, updated)}
                  onDeleteGroup={() => handleDeleteGroup(gIdx)}
                  isFirstGroup={gIdx === 0}
                  fieldOptions={fieldOptions}
                  filterGroups={filterGroups}
                  extraOperators={extraOperators}
                  extraValueOptions={extraValueOptions}
                  extraSingleValueFields={extraSingleValueFields}
                  extraFreeTextFields={extraFreeTextFields}
                />
              </div>
            ))}

            <div className="pl-[70px] pt-3">
              <button
                type="button"
                onClick={handleAddFilter}
                className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] px-10 flex items-center gap-1 cursor-pointer"
              >
                <PlusIcon className="w-3 h-3 text-verba-blue dark:text-verba-blue-dk" />
                <span className="text-md font-semibold text-verba-blue dark:text-verba-blue-dk">
                  Add Filter
                </span>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="px-[18px] py-[18px] backdrop-blur-[20px] shrink-0 border-t border-border-gray dark:border-border-gray-dk rounded-bl-[8px]">
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            onClick={handleFooterCancel}
            innerText="Cancel"
            disabled={isSaveViewLoading}
            buttonContainerClass="h-[26px] w-[100px] shrink-0 border border-border-gray dark:border-border-gray-dk rounded-md text-md font-semibold text-heading-text dark:text-heading-text-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors bg-transparent cursor-pointer"
            innerTextClass="text-md font-semibold text-heading-text dark:text-heading-text-dk"
          />
          <Button
            type="button"
            onClick={onSave}
            innerText={isSaveViewLoading ? "Saving..." : saveButtonText}
            disabled={
              !hasValidFilters || isSaveViewLoading || !hasUnsavedChanges
            }
            buttonContainerClass="group h-[26px] px-12 shrink-0 bg-verba-indigo dark:bg-verba-indigo-dk border border-verba-indigo dark:border-verba-indigo-dk rounded-md enabled:cursor-pointer disabled:!cursor-not-allowed transition-colors hover:enabled:bg-verba-blue dark:hover:enabled:bg-verba-blue-dk hover:enabled:border-verba-blue dark:hover:enabled:border-verba-blue-dk disabled:opacity-50 disabled:hover:opacity-50"
            innerTextClass="text-md text-pure-white dark:text-pure-white-dk text-md font-semibold group-hover:text-pure-white dark:group-hover:text-verba-indigo-dk"
          />
        </div>
      </div>
    </div>
  );
};
